/**
 * Filtro de Tipo de Evento para KPIs de Agendamento Online
 * 
 * Popover com lista de event types do tenant.
 * Segue o mesmo padrão visual compacto do CrmPeriodFilter.
 * 
 * RELEASE: 2026-02-15
 * VERSION: 1.0.0
 */

import { useState } from 'react';
import { CalendarClock, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useSchedulingEventTypes } from '@/hooks/useSchedulingEventTypes';
import { stripTemplateVariables } from '@/lib/schedulingUtils';

/** Props do componente */
interface SchedulingEventTypeFilterProps {
  value: string | null;
  onChange: (eventTypeId: string | null) => void;
  className?: string;
}

/**
 * Filtro compacto de tipo de evento
 */
export function SchedulingEventTypeFilter({
  value,
  onChange,
  className,
}: SchedulingEventTypeFilterProps) {
  const [open, setOpen] = useState(false);
  const { eventTypes, isLoading } = useSchedulingEventTypes();

  // Detecta se é versão compacta
  const isCompact = className?.includes('h-[22px]');

  // Label do tipo selecionado
  const getLabel = () => {
    if (!value) return 'Todos';
    const found = eventTypes.find(et => et.id === value);
    return found ? stripTemplateVariables(found.title) : 'Todos';
  };

  // Handler para seleção
  const handleSelect = (id: string | null) => {
    onChange(id);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-9 px-3 gap-2 font-normal',
            className
          )}
        >
          <CalendarClock className={cn(isCompact ? 'h-3 w-3' : 'h-4 w-4', 'opacity-70')} />
          <span className="truncate max-w-[100px]">{getLabel()}</span>
          <ChevronDown className={cn(isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3', 'opacity-50')} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandList>
            <CommandGroup heading="Tipo de Evento">
              {/* Opção "Todos" */}
              <CommandItem
                value="todos"
                onSelect={() => handleSelect(null)}
                className="cursor-pointer"
              >
                <div
                  className={cn(
                    "mr-2 h-4 w-4 rounded-full border",
                    value === null
                      ? "border-primary bg-primary"
                      : "border-muted"
                  )}
                >
                  {value === null && (
                    <div className="h-full w-full flex items-center justify-center">
                      <div className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
                    </div>
                  )}
                </div>
                Todos
              </CommandItem>

              {/* Event types do tenant */}
              {eventTypes.map(et => (
                <CommandItem
                  key={et.id}
                  value={et.id}
                  onSelect={() => handleSelect(et.id)}
                  className="cursor-pointer"
                >
                  <div
                    className={cn(
                      "mr-2 h-4 w-4 rounded-full border",
                      value === et.id
                        ? "border-primary bg-primary"
                        : "border-muted"
                    )}
                  >
                    {value === et.id && (
                      <div className="h-full w-full flex items-center justify-center">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
                      </div>
                    )}
                  </div>
                  <span className="truncate">{stripTemplateVariables(et.title)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
